import ipRegex from 'ip-regex';
import ipaddr from 'ipaddr.js';
import { Netmask } from 'netmask';
import { z } from 'zod';

export const IPV6_INVALID = 'IPv6 is not supported, please use an IPv4 address';
export const IPV4_INVALID = 'IPv4 is not supported, please use an IPv6 address';
export const INVALID_IPV4 = 'Invalid address, please use IPv4 format';
export const INVALID_IPV6 = 'Invalid address, please use IPv6 format';
export const NOT_IN_NETWORK = 'Not within network range';

export function makeOverlayIPv4Schema(networkCIDRs: readonly string[]) {
  return z
    .string()
    .trim()
    .refine((ipAddress) => !ipRegex.v6({ exact: true }).test(ipAddress), IPV6_INVALID)
    .refine((ipAddress) => ipRegex.v4({ exact: true }).test(ipAddress), INVALID_IPV4)
    .pipe(
      z.string().superRefine((ipAddress, ctx) => {
        const cidrs = networkCIDRs.filter((n) => n.length);
        // No v4 CIDRs means the selected network is v6-only; server validates.
        if (cidrs.length === 0) return;

        // The IP address should exist inside of at least one of the networks, this loosens the validation since we can't tell which network CIDR is going in the host cert.
        const networks = cidrs.map((networkCIDR) => new Netmask(networkCIDR));
        const isIPValid = (netmask: Netmask) => netmask.contains(ipAddress) && ipAddress !== netmask.base;
        // Is the IP valid for at least *one* network cidr?
        const hasOneMatchingNetwork = networks.some((network) => isIPValid(network));

        if (!hasOneMatchingNetwork) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: NOT_IN_NETWORK,
            fatal: true,
          });
          return z.NEVER;
        }
      })
    )

    .or(z.literal(''));
}

export function makeOverlayIPv6Schema(networkCIDRs: readonly string[]) {
  return (
    z
      .string()
      .trim()
      .refine((ipAddress) => !ipRegex.v4({ exact: true }).test(ipAddress), IPV4_INVALID)
      .refine((ipAddress) => ipRegex.v6({ exact: true }).test(ipAddress), INVALID_IPV6)
      // Reject IPv4-mapped IPv6 literals (e.g. ::ffff:10.0.0.1); they are v6-shaped
      // but represent a v4 address and should not be accepted in the v6 field.
      // ipaddr.IPv6.parse is stricter than ipRegex, so wrap in try/catch and let
      // malformed-but-regex-valid inputs fall through to later stages.
      .refine((ipAddress) => {
        try {
          return !ipaddr.IPv6.parse(ipAddress).isIPv4MappedAddress();
        } catch {
          return true;
        }
      }, IPV4_INVALID)
      .pipe(
        z.string().superRefine((ipAddress, ctx) => {
          const cidrs = networkCIDRs.filter((n) => n.length);
          // No v6 CIDRs means the selected network is v4-only; server validates.
          if (cidrs.length === 0) return;

          // The IP address should exist inside of at least one of the networks, this loosens the validation since we can't tell which network CIDR is going in the host cert.
          const ip = ipaddr.IPv6.parse(ipAddress);
          const networks = cidrs.map((networkCIDR) => ipaddr.IPv6.parseCIDR(networkCIDR));
          const isIPValid = ([networkBase, bits]: [ipaddr.IPv6, number]) =>
            ip.match([networkBase, bits]) && ip.toNormalizedString() !== networkBase.toNormalizedString();
          // Is the IP valid for at least *one* network cidr?
          const hasOneMatchingNetwork = networks.some((network) => isIPValid(network));

          if (!hasOneMatchingNetwork) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: NOT_IN_NETWORK,
              fatal: true,
            });
            return z.NEVER;
          }
        })
      )
      .or(z.literal(''))
  );
}
